import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminNav } from '../../components/AdminNav';
import { RefreshCw, Download, Link2, Database, CheckCircle, AlertTriangle, Upload, FileSpreadsheet, Inbox, MapPin, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const AdminSync = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importErr, setImportErr] = useState('');
  const [importStates, setImportStates] = useState(['FL', 'TX']);
  const [selectedState, setSelectedState] = useState('TX');
  const [boards, setBoards] = useState(null);
  const [reqSending, setReqSending] = useState(false);
  const [reqMsg, setReqMsg] = useState('');
  const [inbox, setInbox] = useState(null);
  const [inboxFetching, setInboxFetching] = useState(false);
  const [inboxMsg, setInboxMsg] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushMsg, setPushMsg] = useState('');
  const [geo, setGeo] = useState(null);
  const [geoMsg, setGeoMsg] = useState('');
  const [rebuild, setRebuild] = useState(null);
  const [rebuildMsg, setRebuildMsg] = useState('');
  const [enrich, setEnrich] = useState(null);
  const [enrichMsg, setEnrichMsg] = useState('');
  const [enrichScope, setEnrichScope] = useState('nocity');
  const [enrichState, setEnrichState] = useState('');
  const [enrichPreview, setEnrichPreview] = useState(null);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const fileRef = React.useRef(null);
  const { token, isAuthenticated, loading: authLoading } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchStatus();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/sync/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load sync status');
      setStatus(await res.json());
      const sres = await fetch(`${API}/api/admin/import/states`, { headers: { Authorization: `Bearer ${token}` } });
      if (sres.ok) {
        const sd = await sres.json();
        if (sd.states?.length) setImportStates(sd.states);
      }
      const bres = await fetch(`${API}/api/admin/records/boards`, { headers: { Authorization: `Bearer ${token}` } });
      if (bres.ok) setBoards(await bres.json());
      const ires = await fetch(`${API}/api/admin/records/inbox`, { headers: { Authorization: `Bearer ${token}` } });
      if (ires.ok) setInbox(await ires.json());
      const cres = await fetch(`${API}/api/admin/coverage`, { headers: { Authorization: `Bearer ${token}` } });
      if (cres.ok) setCoverage(await cres.json());
      const gres = await fetch(`${API}/api/admin/geocode/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (gres.ok) setGeo(await gres.json());
      const rres = await fetch(`${API}/api/admin/rebuild/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (rres.ok) setRebuild(await rres.json());
      const eres = await fetch(`${API}/api/admin/enrich/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (eres.ok) setEnrich(await eres.json());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const runSync = async (full) => {
    setRunning(true); setMsg(''); setError('');
    try {
      const res = await fetch(`${API}/api/admin/sync/run?full=${full}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) {
        setMsg(`Sync complete — created ${data.created}, updated ${data.updated}, linked ${data.linked}, errors ${data.errors}.`);
      } else {
        setError(`Sync failed: ${data.error}`);
      }
      fetchStatus();
    } catch (e) { setError(e.message); } finally { setRunning(false); }
  };

  const pushToCf = async (full) => {
    setPushing(true); setPushMsg('');
    try {
      const res = await fetch(`${API}/api/admin/sync/push?full=${full}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Push failed');
      if (data.started === false) {
        setPushMsg('A push is already running — counts update below as it progresses.');
      } else {
        setPushMsg('Push started in background. CF ingest is slow (~1s/record), so counts update gradually below.');
      }
      // Poll status to reflect live progress
      let n = 0;
      const poll = setInterval(async () => {
        n += 1;
        await fetchStatus();
        if (n >= 30) clearInterval(poll);
      }, 6000);
      fetchStatus();
    } catch (err) { setPushMsg(`Error: ${err.message}`); } finally { setPushing(false); }
  };

  const sendAllRecords = async () => {
    setReqSending(true); setReqMsg('');
    try {
      const res = await fetch(`${API}/api/admin/records/send-all`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setReqMsg(`Sent ${data.sent}/${data.attempted} records requests. Boards will reply to ${(boards?.reply_to || []).join(' & ')}.`);
      fetchStatus();
    } catch (err) { setReqMsg(`Error: ${err.message}`); } finally { setReqSending(false); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString('en-US') : 'Never');
  const c = status?.counts || {};

  const fetchInbox = async () => {
    setInboxFetching(true); setInboxMsg('');
    try {
      const res = await fetch(`${API}/api/admin/records/inbox/fetch`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fetch failed');
      setInboxMsg(`Scanned ${data.emails_scanned} email(s), processed ${data.files_processed} file(s) — created ${data.created}, updated ${data.updated}, errors ${data.errors}.`);
      fetchStatus();
    } catch (err) { setInboxMsg(`Error: ${err.message}`); } finally { setInboxFetching(false); }
  };

  const downloadTemplate = async () => {
    const res = await fetch(`${API}/api/admin/import/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'auctioneer-import-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportErr(''); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/admin/import/csv`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setImportResult(data);
      fetchStatus();
    } catch (err) {
      setImportErr(err.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const importFlorida = async () => {
    setImporting(true); setImportErr(''); setImportResult(null);
    try {
      const res = await fetch(`${API}/api/admin/import/state?code=${selectedState}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'State import failed');
      setImportResult({ ...data, rows: (data.created || 0) + (data.updated || 0) + (data.skipped || 0) });
      fetchStatus();
    } catch (err) {
      setImportErr(err.message);
    } finally {
      setImporting(false);
    }
  };

  const runImportState = async (code) => {
    setImporting(true); setImportErr(''); setImportResult(null);
    try {
      const res = await fetch(`${API}/api/admin/import/state?code=${code}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'State import failed');
      setImportResult({ ...data, rows: (data.created || 0) + (data.updated || 0) + (data.skipped || 0) });
      fetchStatus();
    } catch (err) { setImportErr(err.message); } finally { setImporting(false); }
  };

  const runRequestState = async (code) => {
    setReqSending(true); setReqMsg('');
    try {
      const res = await fetch(`${API}/api/admin/records/send/${code}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setReqMsg(`${code}: records request ${data.status || (data.ok ? 'sent' : 'failed')}.`);
      fetchStatus();
    } catch (err) { setReqMsg(`Error: ${err.message}`); } finally { setReqSending(false); }
  };

  const runGeocode = async () => {
    setGeoMsg('');
    try {
      const res = await fetch(`${API}/api/admin/geocode/backfill`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Backfill failed');
      setGeoMsg(data.started === false ? 'A backfill is already running — counts update below.' : 'Geocode backfill started in background — counts update below as it runs.');
      let n = 0;
      const poll = setInterval(async () => { n += 1; await fetchStatus(); if (n >= 30) clearInterval(poll); }, 6000);
      fetchStatus();
    } catch (err) { setGeoMsg(`Error: ${err.message}`); }
  };


  const runRebuild = async () => {
    if (!window.confirm('Rebuild the directory from bundled seed snapshots (5,246 companies + 19,410 auctioneers) + a full CharityFundraiser pull? This is idempotent (no duplicates) and runs in the background.')) return;
    setRebuildMsg('');
    try {
      const res = await fetch(`${API}/api/admin/rebuild/run`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Rebuild failed');
      setRebuildMsg(data.started === false ? 'A rebuild is already running — counts update below.' : 'Rebuild started in background — this can take several minutes. Counts update below as it runs.');
      let n = 0;
      const poll = setInterval(async () => { n += 1; await fetchStatus(); if (n >= 40) clearInterval(poll); }, 8000);
      fetchStatus();
    } catch (err) { setRebuildMsg(`Error: ${err.message}`); }
  };

  const qs = () => `scope=${enrichScope}${enrichState ? `&state=${enrichState}` : ''}`;

  const runEnrichPreview = async () => {
    setEnrichBusy(true); setEnrichMsg(''); setEnrichPreview(null);
    try {
      const res = await fetch(`${API}/api/admin/enrich/preview?${qs()}&limit=15`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Preview failed');
      setEnrichPreview(data);
    } catch (err) { setEnrichMsg(`Error: ${err.message}`); } finally { setEnrichBusy(false); }
  };

  const runEnrich = async () => {
    if (!window.confirm(`Enrich "${enrichScope}"${enrichState ? ` in ${enrichState}` : ' (all states)'} via Google Places? This spends on your Google billing (~$0.03–0.04 per matched record). Only empty city/phone/website are filled; existing data is never overwritten.`)) return;
    setEnrichMsg('');
    try {
      const res = await fetch(`${API}/api/admin/enrich/run?${qs()}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Enrich failed');
      setEnrichMsg(data.started === false ? 'Enrichment already running — progress updates below.' : 'Enrichment started — progress updates below.');
      let n = 0;
      const poll = setInterval(async () => { n += 1; await fetchStatus(); if (n >= 120) clearInterval(poll); }, 5000);
      fetchStatus();
    } catch (err) { setEnrichMsg(`Error: ${err.message}`); }
  };


  const importable = new Set(importStates || []);
  const boardByState = {};
  (boards?.boards || []).forEach((b) => { boardByState[b.state] = b; });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-sync-page">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">CharityFundraiser Two-Way Sync</h2>
          <div className="flex gap-3">
            <button onClick={() => runSync(false)} disabled={running} data-testid="sync-incremental-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} /> {running ? 'Syncing…' : 'Sync Now'}
            </button>
            <button onClick={() => runSync(true)} disabled={running} data-testid="sync-full-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition disabled:opacity-50">
              <Download className="w-4 h-4" /> Full Resync
            </button>
          </div>
        </div>

        {msg && <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded text-green-700" data-testid="sync-msg">{msg}</div>}
        {error && <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded text-red-700" data-testid="sync-error">{error}</div>}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading sync status…</div>
        ) : (
          <>
            <div className="bg-white rounded-xl border-2 border-indigo-300 p-5 mb-6" data-testid="rebuild-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">Rebuild &amp; Reconcile Directory</span>
                </div>
                <button onClick={runRebuild} disabled={rebuild?.running} data-testid="rebuild-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${rebuild?.running ? 'animate-spin' : ''}`} /> {rebuild?.running ? 'Rebuilding…' : 'Rebuild from sources'}
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Rebuilds this environment's directory from bundled seed snapshots (5,246 companies + 19,410 auctioneers) plus a full CharityFundraiser pull. Idempotent (safe to re-run, no duplicates). Use this on a fresh production deploy to populate it to an exact match — no database copy required.
                {' '}Current: <strong>{(rebuild?.counts?.auctioneers || 0).toLocaleString()}</strong> auctioneers · <strong>{(rebuild?.counts?.auction_houses || 0).toLocaleString()}</strong> companies.
                {rebuild && !rebuild.seeds_present && <span className="text-amber-600"> (seed files not found in this environment)</span>}
                {rebuild?.running && <span className="ml-2 text-indigo-600 font-medium" data-testid="rebuild-running">● running…</span>}
              </p>
              <p className="text-sm text-gray-500 mt-1">Last rebuild: <strong>{fmt(rebuild?.last_success_at)}</strong></p>
              {rebuildMsg && <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-2" data-testid="rebuild-msg">{rebuildMsg}</div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 text-gray-500 text-sm"><Database className="w-4 h-4" /> Net Auctions (native)</div>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-testid="count-native">{(c.net_auctions_native || 0).toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 text-gray-500 text-sm"><Download className="w-4 h-4" /> From CharityFundraiser</div>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-testid="count-cf">{(c.from_charityfundraiser || 0).toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 text-gray-500 text-sm"><Link2 className="w-4 h-4" /> Cross-linked (in both)</div>
                <p className="text-3xl font-bold text-gray-900 mt-2" data-testid="count-linked">{(c.cross_linked || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-emerald-200 p-5 mb-6" data-testid="cf-push-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-gray-900">Push Native Listings → CharityFundraiser</span>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => pushToCf(false)} disabled={pushing || !status?.cf_push?.configured} data-testid="cf-push-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                    <Upload className={`w-4 h-4 ${pushing ? 'animate-pulse' : ''}`} /> {pushing ? 'Pushing…' : 'Push new'}
                  </button>
                  <button onClick={() => pushToCf(true)} disabled={pushing || !status?.cf_push?.configured} data-testid="cf-push-all-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition disabled:opacity-50">
                    Re-push all
                  </button>
                </div>
              </div>
              {!status?.cf_push?.configured ? (
                <p className="text-sm text-amber-600">Not configured — set <code>CF_WRITE_API_KEY</code> in backend/.env to enable pushing.</p>
              ) : (
                <p className="text-sm text-gray-600">
                  <strong>{(status?.cf_push?.pushable || 0).toLocaleString()}</strong> eligible ·{' '}
                  <strong className="text-emerald-700">{(status?.cf_push?.pushed || 0).toLocaleString()}</strong> pushed ·{' '}
                  <strong className="text-amber-600">{(status?.cf_push?.pending || 0).toLocaleString()}</strong> pending · auto-pushes daily at 3 AM.
                  {status?.cf_push?.running && <span className="ml-2 text-emerald-600 font-medium" data-testid="cf-push-running">● pushing…</span>}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">Last push: <strong>{fmt(status?.cf_push?.last_success_at)}</strong></p>
              {pushMsg && <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-2" data-testid="cf-push-msg">{pushMsg}</div>}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
              <div className="flex items-center gap-2 mb-2">
                {status?.configured ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                <span className="font-semibold text-gray-900">
                  {status?.configured ? 'CF API key configured' : 'CF API key not configured'}
                </span>
              </div>
              <p className="text-sm text-gray-600">Last successful sync: <strong>{fmt(status?.last_success_at)}</strong></p>
              <p className="text-sm text-gray-500 mt-1">Automatic sync runs every 15 minutes. New auctioneers on either site appear on the other within that window.</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="csv-import-card">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-gray-900">Bulk CSV Import</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Add auctioneers from any public dataset (state license exports, association rosters, purchased lists).
                Columns: name, business_name, city, state, zip, phone, email, website, specialties, certifications, bio, license_number.
                Duplicates are matched by license number or name+city+state and updated in place.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={downloadTemplate} data-testid="import-template-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition">
                  <Download className="w-4 h-4" /> Download template
                </button>
                <input ref={fileRef} type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="import-file-input" />
                <button onClick={() => fileRef.current && fileRef.current.click()} disabled={importing} data-testid="import-upload-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                  <Upload className={`w-4 h-4 ${importing ? 'animate-pulse' : ''}`} /> {importing ? 'Importing…' : 'Upload CSV'}
                </button>
                <div className="inline-flex items-center gap-2">
                  <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} data-testid="state-import-select"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    {importStates.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={importFlorida} disabled={importing} data-testid="import-state-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                    <Download className="w-4 h-4" /> Import state license file
                  </button>
                </div>
              </div>
              {importErr && <div className="mt-3 text-sm text-red-600 bg-red-50 rounded p-2" data-testid="import-error">{importErr}</div>}
              {importResult && (
                <div className="mt-3 text-sm text-green-700 bg-green-50 rounded p-2" data-testid="import-result">
                  Imported {importResult.rows} rows — created {importResult.created}, updated {importResult.updated}, skipped {importResult.skipped}, errors {importResult.errors}.
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="records-request-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">Automated State Records Requests</span>
                </div>
                <button onClick={sendAllRecords} disabled={reqSending} data-testid="records-send-all-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                  <Upload className={`w-4 h-4 ${reqSending ? 'animate-pulse' : ''}`} /> {reqSending ? 'Sending…' : 'Send all requests'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Emails each state board a formal Open-Records request for their licensee list.
                Replies go to {(boards?.reply_to || []).join(' & ')}. Form-only boards are flagged as manual.
              </p>
              {reqMsg && <div className="mb-3 text-sm text-gray-700 bg-gray-50 rounded p-2" data-testid="records-msg">{reqMsg}</div>}
              <div className="overflow-hidden rounded-lg border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50"><tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">State</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Board</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Method</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100" data-testid="records-boards">
                    {(boards?.boards || []).map((b) => (
                      <tr key={b.state}>
                        <td className="px-3 py-2 font-semibold text-gray-800">{b.state}</td>
                        <td className="px-3 py-2 text-gray-600">{b.board}</td>
                        <td className="px-3 py-2">
                          {b.method === 'email'
                            ? <span className="text-green-700">email</span>
                            : <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">manual form ↗</a>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={b.status === 'sent' ? 'text-green-700' : b.status === 'failed' ? 'text-red-600' : 'text-gray-400'}>
                            {b.status}{b.sent_at ? ` · ${fmt(b.sent_at)}` : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="imap-inbox-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">Auto-Inbox Ingestion (IMAP)</span>
                </div>
                <button onClick={fetchInbox} disabled={inboxFetching || !inbox?.configured} data-testid="inbox-fetch-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${inboxFetching ? 'animate-spin' : ''}`} /> {inboxFetching ? 'Checking…' : 'Check inbox now'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Scans <strong>{inbox?.email || 'the records inbox'}</strong> hourly for CSV/Excel attachments from state boards,
                parses + imports them automatically, then files the email under the “Processed” label.
                {inbox && !inbox.configured && <span className="text-amber-600"> (Not configured)</span>}
              </p>
              {inboxMsg && <div className="mb-3 text-sm text-gray-700 bg-gray-50 rounded p-2" data-testid="inbox-msg">{inboxMsg}</div>}
              <p className="text-sm text-gray-500">Last check: <strong>{fmt(inbox?.last_success_at)}</strong></p>
              {(inbox?.recent_runs || []).length > 0 && (
                <div className="mt-3 overflow-hidden rounded-lg border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Checked</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Emails</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Files</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Created</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Updated</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Errors</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100" data-testid="inbox-runs">
                      {inbox.recent_runs.map((r, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-gray-700">{fmt(r.finished_at)}</td>
                          <td className="px-3 py-2 text-gray-700">{r.emails_scanned ?? '—'}</td>
                          <td className="px-3 py-2 text-gray-700">{r.files_processed ?? '—'}</td>
                          <td className="px-3 py-2 text-gray-700">{r.created ?? 0}</td>
                          <td className="px-3 py-2 text-gray-700">{r.updated ?? 0}</td>
                          <td className="px-3 py-2 text-gray-700">{r.errors ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border-2 border-fuchsia-200 p-5 mb-6" data-testid="enrich-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-fuchsia-600" />
                  <span className="font-semibold text-gray-900">Enrich Records (Google Places)</span>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <select value={enrichScope} onChange={(e) => setEnrichScope(e.target.value)} data-testid="enrich-scope-select"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-fuchsia-500 focus:outline-none">
                    <option value="nocity">Missing city (location gap)</option>
                    <option value="thin">Thin (missing city or phone)</option>
                    <option value="premier">Premier members</option>
                  </select>
                  <select value={enrichState} onChange={(e) => setEnrichState(e.target.value)} data-testid="enrich-state-select"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-fuchsia-500 focus:outline-none">
                    <option value="">All states</option>
                    {(coverage?.states || []).map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
                  </select>
                  <button onClick={runEnrichPreview} disabled={enrichBusy} data-testid="enrich-preview-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition disabled:opacity-50">
                    {enrichBusy ? 'Checking…' : 'Preview (free)'}
                  </button>
                  <button onClick={runEnrich} disabled={enrich?.running} data-testid="enrich-run-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                    <Sparkles className={`w-4 h-4 ${enrich?.running ? 'animate-pulse' : ''}`} /> {enrich?.running ? 'Enriching…' : 'Run enrichment'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Fills empty <strong>city, phone, website &amp; address</strong> from Google Places using a strict match guard
                (same state + surname match, so a wrong business is never attached). Existing data is never overwritten.
                Costs ~$0.03–0.04 per matched record on your Google billing.
                {enrich?.running && <span className="ml-2 text-fuchsia-600 font-medium" data-testid="enrich-running">● running…</span>}
              </p>
              {enrich && (enrich.processed > 0 || enrich.running) && (
                <p className="text-sm text-gray-700 mt-2" data-testid="enrich-progress">
                  Processed <strong>{enrich.processed}</strong>/{enrich.total} · matched <strong className="text-emerald-700">{enrich.matched}</strong> · written <strong>{enrich.written}</strong> · skipped {enrich.skipped} · errors {enrich.errors}
                  {enrich.scope ? ` · ${enrich.scope}` : ''}
                </p>
              )}
              {enrichMsg && <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-2" data-testid="enrich-msg">{enrichMsg}</div>}
              {enrichPreview && (
                <div className="mt-3 overflow-hidden rounded-lg border border-gray-100" data-testid="enrich-preview">
                  <div className="px-3 py-2 bg-fuchsia-50 text-sm text-gray-700">
                    Dry-run: <strong>{enrichPreview.matched}</strong>/{enrichPreview.sampled} would match (no data written).
                  </div>
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Licensee</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Google match</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Result</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {enrichPreview.samples.map((s, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-gray-800">{s.name}</td>
                          <td className="px-3 py-2 text-gray-600">{s.google_name || '—'}</td>
                          <td className="px-3 py-2">
                            <span className={s.matched ? 'text-emerald-700' : 'text-gray-400'}>{s.matched ? '✓ match' : s.reason}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="geocode-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">Geolocation Backfill</span>
                </div>
                <button onClick={runGeocode} disabled={geo?.running} data-testid="geocode-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition disabled:opacity-50">
                  <MapPin className="w-4 h-4" /> {geo?.running ? 'Backfilling…' : 'Backfill coordinates'}
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Accurate map coordinates via US Census (street addresses) → ZIP centroid → city.
                {' '}<strong className="text-emerald-700">{(geo?.geocoded || 0).toLocaleString()}</strong> geocoded ·{' '}
                <strong className="text-amber-600">{(geo?.needs_geocode || 0).toLocaleString()}</strong> need coordinates · auto-runs daily at 4 AM.
                {geo?.running && <span className="ml-2 text-indigo-600 font-medium" data-testid="geocode-running">● running…</span>}
              </p>
              <p className="text-sm text-gray-500 mt-1">Last run: <strong>{fmt(geo?.last_success_at)}</strong></p>
              {geoMsg && <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-2" data-testid="geocode-msg">{geoMsg}</div>}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="coverage-card">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-gray-900">Per-State Coverage</span>
                </div>
                {coverage && (
                  <span className="text-sm text-gray-600" data-testid="coverage-summary">
                    {coverage.states_covered}/{coverage.states_total} states covered · {(coverage.total || 0).toLocaleString()} auctioneers · {coverage.states_needing_data} need data
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3">Amber = fewer than 25 listings (needs a data source). Hover a state to see its sources.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="coverage-grid">
                {(coverage?.states || []).map((s) => (
                  <div key={s.code} title={(s.sources || []).join(', ') || 'no data'}
                    className={`rounded-lg border p-2 text-sm ${s.needs_data ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800">{s.code}</span>
                      {s.needs_data ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                    </div>
                    <div className="text-gray-700">{s.count.toLocaleString()}</div>
                    {s.needs_data && (
                      importable.has(s.code) ? (
                        <button data-testid={`coverage-action-${s.code}`} onClick={() => runImportState(s.code)} disabled={importing}
                          className="mt-1 w-full text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Import</button>
                      ) : boardByState[s.code]?.method === 'email' ? (
                        <button data-testid={`coverage-action-${s.code}`} onClick={() => runRequestState(s.code)} disabled={reqSending}
                          className="mt-1 w-full text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Request</button>
                      ) : boardByState[s.code]?.url ? (
                        <a data-testid={`coverage-action-${s.code}`} href={boardByState[s.code].url} target="_blank" rel="noopener noreferrer"
                          className="mt-1 block text-center text-xs px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600">Manual ↗</a>
                      ) : (
                        <span className="mt-1 block text-center text-xs text-gray-400">no source</span>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-3">Recent Sync Runs</h3>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Finished</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Mode</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Created</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Updated</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Linked</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Errors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-testid="sync-runs">
                  {(status?.recent_runs || []).length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No sync runs yet.</td></tr>
                  ) : (
                    status.recent_runs.map((r, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-gray-700">{fmt(r.finished_at)}</td>
                        <td className="px-4 py-3 text-gray-600">{r.mode}</td>
                        <td className="px-4 py-3 text-gray-700">{r.created}</td>
                        <td className="px-4 py-3 text-gray-700">{r.updated}</td>
                        <td className="px-4 py-3 text-gray-700">{r.linked}</td>
                        <td className="px-4 py-3 text-gray-700">{r.errors}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
